import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { Pencil01, Trash01 } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';

import { IconButton } from '../../../components/buttons/icon-button';
import { ImageIcon } from '../../../components/icons/image-icon';
import { tNamespace } from '../../../services/translation';
import { setupParagraphAttributes } from './content-paragraph';

const t = tNamespace('shared.tiptap-custom-extensions.content-image-view');

export function ContentImageView(props: NodeViewProps) {
  const { extension } = props;
  const { src, alt } = props.node.attrs;
  const [innerSrc, setInnerSrc] = useState('');
  const [isError, setIsError] = useState(false);
  const [isDirectChildOfDoc, setIsDirectChildOfDoc] = useState(false);

  useEffect(
    function () {
      const timeoutId = setTimeout(function () {
        setupParagraphAttributes(props.getPos, props.node, props.editor);
      }, 100);
      // check if the image is child of another component
      const pos = props.getPos();
      if (typeof pos === 'number') {
        const resolvedPos = props.editor.state.doc.resolve(pos);
        if (resolvedPos.parent != null) {
          setIsDirectChildOfDoc(resolvedPos.parent.type.name === 'doc');
        }
      }
      // try to load the image separately so we know if the src works or not
      const imgLoader = document.createElement('img');
      imgLoader.src = typeof src === 'string' ? src : '';
      imgLoader.addEventListener('load', function () {
        setInnerSrc(src as string);
      });
      imgLoader.addEventListener('error', function () {
        setIsError(true);
      });
      return function () {
        clearTimeout(timeoutId);
      };
    },
    [props, src]
  );

  function editImage() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (typeof extension?.options?.onEdit !== 'function') {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    extension.options.onEdit(props.getPos(), src, alt);
  }

  return (
    <NodeViewWrapper
      className={`group relative flex items-start justify-end justify-self-center ${isDirectChildOfDoc ? '' : 'py-2'}`}
    >
      {isDirectChildOfDoc ? (
        <div
          className="absolute -left-7 top-0 text-branding-gray-600 group-hover:hidden"
          contentEditable={false}
        >
          <ImageIcon />
        </div>
      ) : null}
      <div className="group relative w-full max-w-[800px]">
        <div className="absolute right-4 top-4 hidden items-center justify-center gap-2.5 group-hover:flex">
          <IconButton variant="accent" size="sm" icon={Pencil01} onClick={editImage} />
          <IconButton variant="accent" size="sm" icon={Trash01} onClick={props.deleteNode} />
        </div>
        {isError ? (
          <div className="grid h-40 w-full place-content-center place-items-center gap-4 rounded-lg bg-branding-gray-100">
            <ImageIcon width={24} height={24} className="text-branding-accent-950" />
            <p contentEditable={false} className="font-body-2-m text-branding-accent-950">
              {t('cannotLoadImage')}
            </p>
          </div>
        ) : (
          <img src={innerSrc} alt={alt as string} className="rounded-lg" />
        )}
      </div>
    </NodeViewWrapper>
  );
}
